import type { FieldErrors, FieldValues, Path, UseFormSetError } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { translateError } from '../../validation/messages';
import type { ErrorSummaryItem } from '../idsk';
import { isApiError } from '../../api/errors';

export interface FieldOrder<T extends FieldValues> {
  name: Path<T>;
  /** Element id the error summary links to. */
  id: string;
}

/** Error-summary items in the order the fields appear on the page. */
export function summaryItems<T extends FieldValues>(t: TFunction, errors: FieldErrors<T>, order: FieldOrder<T>[]): ErrorSummaryItem[] {
  return order.flatMap(({ name, id }): ErrorSummaryItem[] => {
    const error = name.split('.').reduce<unknown>((acc, key) => (acc as Record<string, unknown> | undefined)?.[key], errors) as
      | { message?: string }
      | undefined;
    const text = translateError(t, error?.message);
    return text ? [{ targetId: id, text }] : [];
  });
}

export function fieldError(t: TFunction, error: { message?: string } | undefined): string | undefined {
  return translateError(t, error?.message);
}

/**
 * Maps problem+json `errors[]` onto form fields (`fieldMap` translates API paths such as "address.postalCode").
 * Returns the messages that could not be attached to a field.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fieldMap: Record<string, Path<T>>,
): string[] {
  if (!isApiError(error)) return [];
  const unmatched: string[] = [];
  for (const fe of error.fieldErrors) {
    const field = fieldMap[fe.field];
    if (field) setError(field, { type: 'server', message: fe.message }, { shouldFocus: false });
    else unmatched.push(fe.message);
  }
  return unmatched;
}
